import { computed, ref, type Ref } from 'vue';
import { find, max, cloneDeep } from 'lodash-es';
import type { QueryObserverResult } from '@tanstack/vue-query';
import type { AxiosResponse } from 'axios';
import type { Airflow } from '@aviata/platform-types';
import type { PassengersData, PassengerKey } from '@aviata/booking-data';
import type { flightEntity } from 'freedom-shared';

import { seatMapLib } from './lib';
import { UNSELECTED_SEAT } from './config';
import type { SelectedSeatsState, SelectedSeat } from './types';

type UseDraftSeatMap = {
  flight: flightEntity.types.MappedFlight;
  seatMapQueries: Ref<
    QueryObserverResult<AxiosResponse<Airflow.SeatMap.Entity>>[]
  >;
  passengersData: PassengersData;
  seatMapStateByFlight: Ref<SelectedSeatsState>;
  passengersKeys: PassengerKey[];
};
export function useDraftSeatMap({
  flight,
  seatMapQueries,
  passengersData,
  seatMapStateByFlight,
  passengersKeys,
}: UseDraftSeatMap) {
  const draftSeatMapStateByFlights = ref<SelectedSeatsState>(
    cloneDeep(seatMapStateByFlight.value),
  );
  const activeSegmentIdx = ref(0);
  const activePassengerKey = ref<PassengerKey>(passengersKeys[0]);
  const activeSegment = computed(() =>
    find(flight.segments, (_, index) => index === activeSegmentIdx.value),
  );
  const activeSegmentState = computed(
    () => draftSeatMapStateByFlights.value[activeSegmentIdx.value],
  );
  const activeSegmentQuery = computed(
    () => seatMapQueries.value[activeSegmentIdx.value],
  );
  const mappedSeatMap = computed(() => {
    const seatMapResponse = activeSegmentQuery.value.data?.data;
    if (seatMapResponse) {
      return seatMapLib.mapSeatMap(seatMapResponse);
    }
    return null;
  });
  const seatMapLegend = computed(() => {
    if (mappedSeatMap.value) {
      return seatMapLib.getSeatMapLegend(mappedSeatMap.value);
    }
    return null;
  });
  const maxSeatsInRow = computed(() => {
    return (
      max(
        mappedSeatMap.value?.rows.flatMap(
          row => Object.values(row.seats).length,
        ),
      ) || 0
    );
  });

  function selectSegment(segmentIdx: number) {
    activeSegmentIdx.value = segmentIdx;
  }

  function selectPassenger(passengerKey: PassengerKey) {
    activePassengerKey.value = passengerKey;
  }

  function updateSeat(updatedSeat: SelectedSeat, passengerKey?: PassengerKey) {
    const seatUpdatedPassengerKey = passengerKey || activePassengerKey.value;
    draftSeatMapStateByFlights.value[activeSegmentIdx.value] = {
      ...activeSegmentState.value,
      [seatUpdatedPassengerKey]: updatedSeat,
    };
  }

  function selectSeat(selectedSeat: SelectedSeat) {
    updateSeat(selectedSeat);
  }

  function unselectSeat(passengerKey?: PassengerKey) {
    updateSeat(UNSELECTED_SEAT, passengerKey);
  }

  function resetState() {
    draftSeatMapStateByFlights.value = cloneDeep(seatMapStateByFlight.value);
  }

  return {
    passengersData,
    draftSeatMapStateByFlights,
    activePassengerKey,
    activeSegment,
    activeSegmentState,
    mappedSeatMap,
    seatMapLegend,
    maxSeatsInRow,
    activeSegmentIdx,
    activeSegmentQuery,
    selectSegment,
    selectPassenger,
    selectSeat,
    unselectSeat,
    resetState,
  };
}
